// UpdateNotifier.jsx — the app keeps ITSELF current (Brian, 2026-08-24).
//
// THE PROBLEM: every deploy left phones/tabs running the old bundle until a
// manual hard refresh — fixes looked broken in the field minutes after they
// shipped ("stale tab bites EVERY deploy", now on phones too).
//
// HOW: poll the build's asset-manifest.json (tiny, never cached) and compare
// the live main.js filename with the one this page loaded.
//   - Update found while the user is ON the page → green banner, tap reloads
//     (never yank the page out from under someone mid-typing).
//   - Coming BACK to the tab after 10+ minutes away → reload automatically
//     (the field case: phone tab reopened days later, nothing in progress —
//     cart and drafts persist in localStorage anyway).
import { useEffect, useRef, useState } from "react";
import { useLang } from "../i18n/LanguageContext";

const CHECK_MS = 5 * 60 * 1000;      // periodic check while the tab is open
const AUTO_RELOAD_HIDDEN_MS = 10 * 60 * 1000; // away this long -> just reload

function currentMainFile() {
  const el = document.querySelector('script[src*="static/js/main"]');
  const src = el?.getAttribute("src") || "";
  return src.split("/").pop() || null;
}

async function latestMainFile() {
  const res = await fetch("/asset-manifest.json", { cache: "no-store" });
  if (!res.ok) return null;
  const man = await res.json();
  const latest = man?.files?.["main.js"] || "";
  return latest.split("/").pop() || null;
}

export default function UpdateNotifier() {
  const { t } = useLang();
  const [updateReady, setUpdateReady] = useState(false);
  const hiddenAt = useRef(null);

  useEffect(() => {
    const loaded = currentMainFile();
    if (!loaded) return; // dev server — no hashed bundle to compare
    let stopped = false;

    async function check({ autoReload }) {
      try {
        const latest = await latestMainFile();
        if (stopped || !latest || latest === loaded) return;
        if (autoReload) window.location.reload();
        else setUpdateReady(true);
      } catch { /* offline — try again later */ }
    }

    const iv = setInterval(() => check({ autoReload: false }), CHECK_MS);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt.current = Date.now();
      } else {
        const away = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
        check({ autoReload: away >= AUTO_RELOAD_HIDDEN_MS });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    check({ autoReload: false });

    return () => {
      stopped = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  if (!updateReady) return null;
  return (
    <button className="update-banner" onClick={() => window.location.reload()}>
      {t("updateReady")}
    </button>
  );
}
